import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, X, Loader2, Lock, MessageCircle } from "lucide-react";
import { trackEvent } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Lightweight markdown renderer: **bold**, bullet lists, headings, line breaks */
function inline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}
export function Markdown({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1.5 leading-relaxed">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (/^#{1,6}\s/.test(t)) return <div key={i} className="font-bold text-[13px] mt-1">{inline(t.replace(/^#{1,6}\s/, ""))}</div>;
        if (/^[-*]\s/.test(t)) return <div key={i} className="flex gap-2"><span className="text-[#FF5500] mt-0.5">•</span><span>{inline(t.replace(/^[-*]\s/, ""))}</span></div>;
        const num = t.match(/^(\d+)\.\s(.*)/);
        if (num) return <div key={i} className="flex gap-2"><span className="text-[#8B5CF6] font-semibold">{num[1]}.</span><span>{inline(num[2])}</span></div>;
        return <div key={i}>{inline(t)}</div>;
      })}
    </div>
  );
}

function sessionKey(mode, uid) {
  const k = `tubmix_ai_sid_${mode}${uid ? "_" + uid : ""}`;
  let sid = localStorage.getItem(k);
  if (!sid) { sid = `${mode}-${crypto.randomUUID()}`; localStorage.setItem(k, sid); }
  return sid;
}

/**
 * AiChat panel body (message list + input). Streams via SSE.
 * props: mode, welcome, placeholder, buildContext?, userId?
 */
export function AiChatPanel({ mode, welcome, placeholder, buildContext, userId }) {
  const { t, lang } = useLang();
  const [sid] = useState(() => sessionKey(mode, userId));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/ai/chat/${sid}`)
      .then((r) => r.json())
      .then((d) => {
        const hist = (d.messages || []).map((m) => ({
          role: m.role,
          content: typeof m.content === "string"
            ? m.content
            : (Array.isArray(m.content) ? m.content.map((c) => c.text || "").join("") : ""),
        }));
        setMessages(hist);
      })
      .catch(() => {});
  }, [sid]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = useCallback(async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    trackEvent("ai_chat", mode);
    try {
      const token = localStorage.getItem("tubmix_token");
      const res = await fetch(`${BACKEND}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({
          session_id: sid, mode, message: msg, lang,
          context: mode === "studio" && buildContext ? buildContext() : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: err.detail || t("ai.error") }; return c; });
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let d;
          try { d = JSON.parse(p.slice(6)); } catch { continue; }
          if (d.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d.delta }; return c; });
          if (d.error) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: t("ai.error") }; return c; });
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: t("ai.error") }; return c; });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, sid, mode, lang, buildContext, t]);

  const lastAssistantEmpty = streaming && messages.length && messages[messages.length - 1].role === "assistant" && !messages[messages.length - 1].content;

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto tx-dark-scroll p-4 space-y-3">
        <div className="flex gap-2.5">
          <div className="h-7 w-7 shrink-0 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center"><Sparkles className="h-4 w-4 text-[#8B5CF6]" /></div>
          <div className="bg-[#1A1F2C] rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm text-[#cbd5e1] max-w-[85%]">{welcome}</div>
        </div>
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${m.role}-${i}`}>
            {m.role === "assistant" && <div className="h-7 w-7 shrink-0 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center"><Sparkles className="h-4 w-4 text-[#8B5CF6]" /></div>}
            <div className={`rounded-2xl px-3.5 py-2.5 text-sm max-w-[85%] ${m.role === "user" ? "bg-[#FF5500] text-white rounded-tr-sm" : "bg-[#1A1F2C] text-[#cbd5e1] rounded-tl-sm"}`}>
              {m.role === "assistant" ? <Markdown text={m.content} /> : m.content}
            </div>
          </div>
        ))}
        {lastAssistantEmpty && (
          <div className="flex gap-2.5 items-center text-[#64748B] text-xs pl-10"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("ai.thinking")}</div>
        )}
      </div>
      <div className="border-t border-[#262C3A] p-3">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="ai-chat-input" rows={1} value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={placeholder}
            className="flex-1 resize-none bg-[#0B0D12] border border-[#262C3A] rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-[#8B5CF6] transition-colors max-h-28"
          />
          <button onClick={send} disabled={streaming || !input.trim()} data-testid="ai-chat-send"
            className="h-10 w-10 shrink-0 rounded-xl bg-[#8B5CF6] hover:bg-[#7c3aed] disabled:opacity-40 flex items-center justify-center transition-colors">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-[10px] text-[#475569] mt-2 text-center font-mono">{t("ai.poweredBy")}</p>
      </div>
    </div>
  );
}

/**
 * Floating launcher + panel. Used on Landing (support) and Studio (studio).
 * props: mode, buildContext?, canUse (studio gate), onUpgrade, userId
 */
export function AiChatWidget({ mode = "support", buildContext, canUse = true, onUpgrade, userId }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const isStudio = mode === "studio";

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ type: "spring", stiffness: 320, damping: 30 }}
            data-testid="ai-chat-panel"
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[75vh] bg-[#0B0D12] border border-[#262C3A] rounded-3xl shadow-2xl overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#262C3A] bg-gradient-to-r from-[#131722] to-[#1A1F2C]">
              <div className="h-9 w-9 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#8B5CF6]" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-sm text-white">{isStudio ? t("ai.studioTitle") : t("ai.supportTitle")}</div>
                <div className="text-[11px] text-[#64748B] truncate">{isStudio ? t("ai.studioSub") : t("ai.supportSub")}</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="text-[#64748B] hover:text-white transition-colors"><X className="h-5 w-5" /></button>
            </div>
            {isStudio && !canUse ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-4">
                <div className="h-14 w-14 rounded-2xl bg-[#8B5CF6]/15 flex items-center justify-center"><Lock className="h-7 w-7 text-[#8B5CF6]" /></div>
                <h3 className="font-heading font-bold text-white">{t("ai.gatedTitle")}</h3>
                <p className="text-sm text-[#94A3B8]">{t("ai.gatedDesc")}</p>
                <button onClick={onUpgrade} data-testid="ai-upgrade-btn" className="bg-[#FF5500] hover:bg-[#E04800] rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors">{t("ai.gatedCta")}</button>
              </div>
            ) : (
              <AiChatPanel
                mode={mode}
                userId={userId}
                buildContext={buildContext}
                welcome={isStudio ? t("ai.studioWelcome") : t("ai.supportWelcome")}
                placeholder={t("ai.placeholder")}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((o) => !o)} data-testid="ai-chat-launcher"
        className="fixed bottom-5 right-4 sm:right-6 z-50 h-14 w-14 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#6366F1] shadow-xl shadow-[#8B5CF6]/30 flex items-center justify-center text-white">
        {open ? <X className="h-6 w-6" /> : (isStudio ? <Sparkles className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />)}
      </motion.button>
    </>
  );
}
